/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { or, readOnly, filterBy } from '@ember/object/computed';
import { isEmpty, isPresent } from '@ember/utils';
import { inject as service } from '@ember/service';
import { set, action, computed } from '@ember/object';
import { task } from 'ember-concurrency';

import { Task } from 'ember-concurrency';
import DeliveryModel from 'cio/models/delivery';
import AnalyticsService from 'cio/services/analytics';
import MessageSettingsService from 'cio/services/message-settings';
import { ActionInfo } from 'cio/utils/action-info';
import { ParamsForAction } from 'cio/components/cio-deliveries';

export type RetryEvent = 'retry_send_canceled' | 'retry_send_started' | 'retry_send_confirmed';

type ActionKey = 'onReload' | 'onDestroy' | 'onRetry' | 'onRedeliver' | 'onSend';

type ModalType = 'Destroy' | 'Retry' | 'Send';

type ModalVisibilityKey = 'showDestroyModal' | 'showRetryModal' | 'showSendModal';

interface ActionOptions {
  overrideLimit?: boolean;
  tracked?: boolean;
}

interface DeliveryRow {
  delivery: DeliveryModel;
  isSelected: boolean;
  selectable: boolean;
}

interface ListArgs {
  continuation: number;
  deliveries: DeliveryModel[];
  fetchDeliveries: Task<unknown, []>;
  fetchMore: Task<unknown, []>;
  actionType: string;
  actionInfo: ActionInfo;
  campaignId?: string;
  actionId?: string;
  detailRoute: string;
  drafted: boolean;
  emptyMessage: string;
  errorMessage: string;
  asFilters: boolean;
  individualPersonBlankSlate: boolean;
  onReload: (params: ParamsForAction) => unknown;
  onDestroy: (params: ParamsForAction) => unknown;
  onRetry: (params: ParamsForAction) => unknown;
  onRedeliver: (params: ParamsForAction) => unknown;
  onSend: (params: ParamsForAction) => unknown;
  hasReadOnlyPermission: boolean;
}

export default class List extends Component<ListArgs> {
  @service analytics!: AnalyticsService;
  @service messageSettings!: MessageSettingsService;

  @tracked showDestroyModal = false;
  @tracked showRetryModal = false;
  @tracked showSendModal = false;

  @filterBy('rows', 'isSelected', true) selectedItems!: DeliveryRow[];

  @or('args.campaignId', 'args.actionId') sendAll!: boolean;

  @readOnly('selectedItems.length') messageCount!: number;

  @computed('args.deliveries.@each.isFailed')
  get anyDeliveryFailedOrUndeliverable() {
    return this.args.deliveries.some((delivery) => delivery.isFailed || delivery.isUndeliverable);
  }

  @computed('selectedItems.[]')
  get onlyDraftedDeliverySelected() {
    return this.selectedItems.length && this.selectedItems.every((item) => item.delivery.isDrafted && item.isSelected);
  }

  @computed('args.deliveries.@each.isDrafted')
  get allDraftDeliveries() {
    return this.args.deliveries.isEvery('isDrafted');
  }

  @computed('args.deliveries.@each.canBeSentOrDeleted')
  get canSelectDeliveries() {
    return this.args.deliveries.isAny('canBeSentOrDeleted');
  }

  @computed('selectedItems.[]', 'args.deliveries.[]')
  get itemLabel() {
    let selectedDeliveries = isEmpty(this.selectedItems)
      ? this.args.deliveries.filterBy('canBeSentOrDeleted')
      : this.selectedItems.mapBy('delivery');

    if (isEmpty(selectedDeliveries)) {
      return;
    }

    let firstDelivery = selectedDeliveries.get('firstObject');
    let firstDeliveryLabel = firstDelivery?.get('label');

    let selectedDeliveriesAreTheSameType = selectedDeliveries.isEvery('label', firstDeliveryLabel);

    if (selectedDeliveriesAreTheSameType) {
      return firstDeliveryLabel;
    } else {
      return 'message';
    }
  }

  // Only failed messages are selectable
  @computed('itemLabel')
  get selectableItemsLabel() {
    return this.itemLabel === 'message' ? 'failed message' : this.itemLabel;
  }

  @computed('args.actionType', 'args.actionInfo.icon')
  get icon() {
    return isEmpty(this.args.actionType) ? 'delivery-drafts' : this.args.actionInfo.icon;
  }

  @computed('args.deliveries.[]')
  get rows(): DeliveryRow[] {
    return this.args.deliveries.map((delivery) => ({
      delivery,
      isSelected: false,
      selectable: delivery.isFailed || delivery.isUndeliverable || delivery.isDrafted,
    }));
  }

  @computed('rows.@each.selectable')
  get _selectableRows() {
    return this.rows.filter((row) => row.selectable);
  }

  @computed('args.deliveries.@each.canBeSentOrDeleted')
  get selectAllDisabled() {
    let { deliveries } = this.args;
    return deliveries.length > 0 ? deliveries.isEvery('canBeSentOrDeleted', false) : false;
  }

  @computed('rows.@each.isSelected')
  get allSelected() {
    return this.rows.isEvery('isSelected');
  }
  set allSelected(value) {
    this.rows.forEach((row) => {
      if (row.selectable) {
        set(row, 'isSelected', value);
      }
    });
  }

  _paramsForSingleAction(actionString: string, delivery: DeliveryModel, options?: ActionOptions): ParamsForAction {
    let params: ParamsForAction = {
      delivery_ids: [delivery.id],
      itemLabel: actionString === 'redeliver' ? `${delivery.label}` : this.itemLabel,
    };

    if (isPresent(options?.tracked)) {
      params.tracked = options?.tracked;
    }

    if (isPresent(options?.overrideLimit)) {
      params.override_message_limit = options?.overrideLimit;
    }

    return params;
  }

  _paramsForBulkAction(options?: ActionOptions): ParamsForAction {
    let { actionId, campaignId } = this.args;
    let params: ParamsForAction = {
      itemLabel: this.itemLabel,
    };

    if (isPresent(options?.overrideLimit)) {
      params.override_message_limit = options?.overrideLimit;
    }

    if (!isEmpty(this.selectedItems)) {
      params.delivery_ids = this.selectedItems.map((item) => item.delivery.id);
    } else if (actionId) {
      if (/^n/.test(actionId)) {
        params.template_id = actionId.substring(1);
      } else {
        params.action_id = actionId;
      }
    } else if (campaignId) {
      if (/^newsletter_/.test(campaignId)) {
        params.newsletter_id = campaignId.replace('newsletter_', '');
      } else {
        params.campaign_id = campaignId;
      }
    }

    return params;
  }

  _trackRetryEvent(eventKey: RetryEvent, additionalParams: { overrideLimit?: boolean } = {}) {
    let params = {
      count: this.selectedItems.length,
      statuses: [...new Set(this.selectedItems.map((item) => item.delivery.displayState))],
      ...additionalParams,
    };

    this.analytics.track('message', eventKey, params);
  }

  @action
  promptBulkRetry() {
    this.showRetryModal = true;
    this._trackRetryEvent('retry_send_started');
  }

  @action
  cancelBulkRetry() {
    this.showRetryModal = false;
    this._trackRetryEvent('retry_send_canceled');
  }

  @action
  confirmBulkRetry(overrideLimit: boolean) {
    this.confirmBulkAction.perform('Retry', { overrideLimit });
    this._trackRetryEvent('retry_send_confirmed', { overrideLimit });
  }

  @action
  onAction(actionString: string, row: DeliveryRow, options?: ActionOptions) {
    if (row.selectable) {
      set(row, 'isSelected', false);
    }

    let params = this._paramsForSingleAction(actionString.toLowerCase(), row.delivery, options);
    this.args[`on${actionString}` as ActionKey](params);
  }

  @action
  submitBulkActionModalForm(type: ModalType, event: Event) {
    event.preventDefault();
    this.confirmBulkAction.perform(type);
  }

  confirmBulkAction = task(async (type: ModalType, options?: ActionOptions) => {
    this[`show${type}Modal` as ModalVisibilityKey] = false;

    try {
      await this.args[`on${type}` as ActionKey](this._paramsForBulkAction(options));
      this.allSelected = false;
    } catch {
      // do nothing
    }
  });
}
